import * as React from 'react';
import {
    TextField,
    Select,
    Box,
    Typography,
    MenuItem,
    Button,
    Paper,
    Stack,
    FormHelperText
} from '@mui/material';
import { useToken } from '../items/UseToken';

export default function AddUser({ handleClose }) {
    const [userEmail, setUserEmail] = React.useState('');
    const [userFirstName, setUserFirstName] = React.useState('');
    const [userType, setUserType] = React.useState('');
    const [errors, setErrors] = React.useState({});
    const { token } = useToken();
    const userTypes = ['Admin', 'User'];

    const validateForm = () => {
        const newErrors = {};

        if (!userEmail.trim()) newErrors.email = 'Email is required.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail))
            newErrors.email = 'Please enter a valid email address.';

        if (!userFirstName.trim()) newErrors.firstName = 'First name is required.';

        if (!userType) newErrors.userType = 'Please select a user type.';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateForm()) return;

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            body: JSON.stringify({ email: userEmail, firstName: userFirstName, userType }),
        };
        fetch('api/newuser', requestOptions).then(() => handleClose());
    };

    const isFormValid = userEmail && userFirstName && userType && Object.keys(errors).length === 0;

    return (
        <Paper
            elevation={3}
            sx={{
                p: 4,
                maxWidth: 480,
                margin: '0 auto',
                borderRadius: 4,
                background: 'linear-gradient(145deg, #ffffff, #f9fafb)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
            }}
        >
            <Typography
                variant="h5"
                sx={{
                    mb: 3,
                    fontWeight: 600,
                    color: '#111827',
                    textAlign: 'center',
                    letterSpacing: 0.3,
                }}
            >
                Add New User
            </Typography>

            <Stack spacing={3}>
                <TextField
                    label="User Email"
                    type="email"
                    variant="outlined"
                    fullWidth
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                />

                <TextField
                    label="First Name"
                    variant="outlined"
                    fullWidth
                    value={userFirstName}
                    onChange={(e) => setUserFirstName(e.target.value)}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                />

                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                        Select User Type
                    </Typography>
                    <Select
                        value={userType}
                        onChange={(e) => setUserType(e.target.value)}
                        displayEmpty
                        fullWidth
                        sx={{
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: errors.userType ? '#ef4444' : '#d1d5db',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: errors.userType ? '#ef4444' : '#6366f1',
                            },
                        }}
                    >
                        <MenuItem disabled value="">
                            <em>Select user type</em>
                        </MenuItem>
                        {userTypes.map((user) => (
                            <MenuItem key={user} value={user}>
                                {user}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.userType && (
                        <FormHelperText error>{errors.userType}</FormHelperText>
                    )}
                </Box>

                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    size="large"
                    disabled={!isFormValid}
                    sx={{
                        mt: 2,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        background: isFormValid
                            ? 'linear-gradient(135deg, #6366f1, #7c3aed)'
                            : 'linear-gradient(135deg, #cbd5e1, #e2e8f0)',
                        color: isFormValid ? 'white' : '#64748b',
                        boxShadow: isFormValid
                            ? '0 6px 18px rgba(99,102,241,0.25)'
                            : 'none',
                        cursor: isFormValid ? 'pointer' : 'not-allowed',
                        '&:hover': {
                            background: isFormValid
                                ? 'linear-gradient(135deg, #4f46e5, #6d28d9)'
                                : 'linear-gradient(135deg, #cbd5e1, #e2e8f0)',
                        },
                    }}
                >
                    Add User
                </Button>
            </Stack>
        </Paper>
    );
}
